import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createOrder } from '../../services/portalApi';

const SERVICE_TYPES = ['maintenance', 'body_damage', 'mechanical', 'other'];

interface PartRow {
  name: string;
  part_number: string;
  quantity: number;
  notes: string;
}

const PortalNewOrderPage: React.FC = () => {
  const { orgAlias } = useParams<{ orgAlias: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [orderName, setOrderName] = useState('');
  const [vin, setVin] = useState('');
  const [serviceType, setServiceType] = useState('other');
  const [parts, setParts] = useState<PartRow[]>([{ name: '', part_number: '', quantity: 1, notes: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const updatePart = (i: number, field: keyof PartRow, value: string | number) => {
    setParts((prev) => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));
  };

  const addPart = () => setParts((prev) => [...prev, { name: '', part_number: '', quantity: 1, notes: '' }]);
  const removePart = (i: number) => setParts((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const filledParts = parts.filter((p) => p.name.trim());
    setSubmitting(true);
    try {
      const order = await createOrder({
        name: orderName,
        vin,
        service_type: serviceType,
        parts_in_order: { items: filledParts },
      });
      navigate(`/portal/${orgAlias}/orders/${order.id}`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('portal.newOrder.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3">
        <Link to={`/portal/${orgAlias}/orders`} className="text-sm text-blue-600 hover:underline">{t('portal.newOrder.backToOrders')}</Link>
        <span className="text-gray-300">/</span>
        <span className="text-sm font-medium text-gray-700">{t('portal.newOrder.breadcrumb')}</span>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('portal.newOrder.pageTitle')}</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Order info */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
            <h2 className="font-semibold text-gray-800">{t('portal.newOrder.sectionDetails')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('portal.newOrder.labelOrderName')}</label>
                <input value={orderName} onChange={(e) => setOrderName(e.target.value)} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('portal.newOrder.placeholderOrderName')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('portal.newOrder.labelVin')}</label>
                <input value={vin} onChange={(e) => setVin(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('portal.newOrder.placeholderVin')} maxLength={17} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('portal.newOrder.labelServiceType')}</label>
                <select value={serviceType} onChange={(e) => setServiceType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm capitalize focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Parts */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-800">{t('portal.newOrder.sectionParts')}</h2>
              <button type="button" onClick={addPart}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                {t('portal.newOrder.addPart')}
              </button>
            </div>
            <div className="space-y-3">
              {parts.map((part, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-4">
                    {i === 0 && <label className="block text-xs text-gray-500 mb-1">{t('portal.newOrder.colPartName')}</label>}
                    <input value={part.name} onChange={(e) => updatePart(i, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={t('portal.newOrder.placeholderPartName')} />
                  </div>
                  <div className="col-span-3">
                    {i === 0 && <label className="block text-xs text-gray-500 mb-1">{t('portal.newOrder.colPartNo')}</label>}
                    <input value={part.part_number} onChange={(e) => updatePart(i, 'part_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="OEM-1234" />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <label className="block text-xs text-gray-500 mb-1">{t('portal.newOrder.colQty')}</label>}
                    <input type="number" min={1} value={part.quantity}
                      onChange={(e) => updatePart(i, 'quantity', parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <label className="block text-xs text-gray-500 mb-1">{t('portal.newOrder.colNotes')}</label>}
                    <input value={part.notes} onChange={(e) => updatePart(i, 'notes', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={t('portal.newOrder.placeholderNotes')} />
                  </div>
                  <div className="col-span-1 flex items-end pb-0.5">
                    {i === 0 && <div className="h-5 mb-1" />}
                    {parts.length > 1 && (
                      <button type="button" onClick={() => removePart(i)}
                        className="text-red-400 hover:text-red-600 p-2 rounded transition-colors">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>}

          <div className="flex gap-3 justify-end">
            <Link to={`/portal/${orgAlias}/orders`}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors">
              {t('portal.newOrder.cancel')}
            </Link>
            <button type="submit" disabled={submitting}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
              {submitting ? t('portal.newOrder.submitting') : t('portal.newOrder.placeOrder')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PortalNewOrderPage;
